import { useEffect, useMemo, useRef, useState } from 'react';
import {
  NJAccordionItem,
  NJAccordionItemContent,
  NJAccordionItemHeader,
  NJBadge,
  NJButton,
  NJCard,
  NJHeading,
  NJIcon,
  NJIconButton,
  NJInlineMessage,
  NJListDeprecated,
  NJListItemDeprecated,
  NJSidebarBrandDeprecated,
  NJSidebarCloseDeprecated,
  NJSidebarDeprecated,
  NJSidebarDividerDeprecated,
  NJSidebarMenuDeprecated,
  NJSpinner,
  NJTab,
  NJTabs,
  NJTag,
  NJText,
  NJTooltip,
} from '@engie-group/fluid-design-system-react';

const PRIMARY_NAV = [
  { id: 'networks', icon: 'account_tree', label: 'Networks' },
  { id: 'calculations', icon: 'legend_toggle', label: 'Calculations' },
  { id: 'coreai', icon: 'auto_awesome', label: 'Nemo CoreAI' },
];
const PRIMARY_FOOTER = [
  { id: 'user-guide', icon: 'help_outline', label: 'User guide' },
  { id: 'profile', icon: 'account_circle', label: 'Profile' },
  { id: 'disconnect', icon: 'power_settings_new', label: 'Disconnect' },
];
const SECONDARY_NAV = [
  { id: 'general', icon: 'remove_red_eye', label: 'General' },
  { id: 'import-export', icon: 'swap_horiz', label: 'Import/export' },
  { id: 'network-editor', icon: 'map', label: 'Network editor' },
  { id: 'production', icon: 'factory', label: 'Production' },
  { id: 'plannings', icon: 'insert_invitation', label: 'Plannings' },
  { id: 'optimizations', icon: 'stacked_bar_chart', label: 'Optimizations' },
];

const PRIMARY_SIDEBAR_WIDTH_OPEN = 230;
const PRIMARY_SIDEBAR_WIDTH_FOLDED = 60;

import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import DataTable from '@shared/DataTable';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import { useOptimizationCloneList } from './hooks';
import NewOptiFormTimelessEauFroide from '@prototypes/size2mix/new-opti-form-timeless-eau-froide/index';
import TrajectoryOptimizationCreateForm from '@prototypes/size2mix/trajectory-optimization-create-form/index';
import NewOptiFormShortTerm from '@prototypes/size2mix/new-opti-form-short-term/index';
import type {
  OptimizationAttribute,
  OptimizationRow,
  OptimizationStatus,
  OptimizationType,
} from './types';

export const meta = {
  title: 'Optimisations - Détail depuis la liste',
  description:
    "Liste des optimisations (layout clone) avec navigation vers la page détail et l'onglet Paramètres.",
  tags: ['optimisation', 'list', 'navigation', 'parameters'],
  author: 'dv5886',
  status: 'draft' as const,
  category: 'optimisations',
};

const STATUS_LABELS: Record<OptimizationStatus, string> = {
  running: 'En cours',
  success: 'Succès',
  preparing: 'En préparation',
  failed: 'Échec',
};

const TYPE_LABELS: Record<OptimizationType, string> = {
  timeless: 'Timeless',
  trajectory: 'Trajectory',
  'short-term': 'Short term',
};

type DetailTab =
  | 'parameters'
  | 'cartographic-results'
  | 'charts'
  | 'synthesis'
  | 'logs';

const DETAIL_TABS: Array<{ id: DetailTab; label: string }> = [
  { id: 'parameters', label: 'Paramètres' },
  { id: 'cartographic-results', label: 'Résultats cartographiques' },
  { id: 'charts', label: 'Graphiques' },
  { id: 'synthesis', label: 'Synthèse' },
  { id: 'logs', label: 'Logs' },
];

const PARAMETERS_SECTIONS = [
  {
    id: 'general-informations',
    title: 'Informations générales',
    rows: [
      { label: 'Name', value: 'Optimisation_287534201' },
      { label: 'Number of steps', value: '20' },
      { label: 'Horizon', value: 'Timeless' },
      { label: 'Granularity', value: 'Horaire' },
    ],
  },
  {
    id: 'input-data',
    title: "Données d'entrée",
    rows: [
      { label: 'Météo', value: 'meteo_historique_2026.csv' },
      { label: 'Prix énergie', value: 'tarifs_energie_2026.xlsx' },
      { label: 'Demandes', value: 'demande_reseau_horaire.csv' },
    ],
  },
  {
    id: 'production-planning',
    title: 'Planning de production',
    rows: [
      { label: 'Actifs planifiés', value: '8' },
      { label: 'Maintenances', value: '3' },
      { label: 'Période', value: '01/01/2026 - 31/12/2026' },
    ],
  },
  {
    id: 'topology-planning',
    title: 'Planning de topologie',
    rows: [
      { label: 'Scénarios topologiques', value: '2' },
      { label: 'Vannes pilotées', value: '14' },
      { label: 'Fréquence de mise à jour', value: 'Toutes les 2h' },
    ],
  },
  {
    id: 'advanced-options',
    title: 'Options avancées',
    rows: [
      { label: 'Solveur', value: 'cbc' },
      { label: 'Tolérance', value: '1e-6' },
      { label: 'Temps max de calcul', value: '1800 s' },
    ],
  },
];

function buildCloneName(sourceName: string): string {
  return `Clone - ${sourceName}`;
}

function toDateTimeLocalValue(raw: string): string {
  return raw.slice(0, 16);
}

function WarningIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <g clipPath="url(#clip0_2552_5421)">
        <path d="M1 21H23L12 2L1 21ZM13 18H11V16H13V18ZM13 14H11V10H13V14Z" fill="#FF6103" fillOpacity="0.73" />
      </g>
      <defs>
        <clipPath id="clip0_2552_5421">
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

function ReportIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <g clipPath="url(#clip0_2552_5424)">
        <path d="M15.73 3H8.27L3 8.27V15.73L8.27 21H15.73L21 15.73V8.27L15.73 3ZM12 17.3C11.28 17.3 10.7 16.72 10.7 16C10.7 15.28 11.28 14.7 12 14.7C12.72 14.7 13.3 15.28 13.3 16C13.3 16.72 12.72 17.3 12 17.3ZM13 13H11V7H13V13Z" fill="#DB3735" />
      </g>
      <defs>
        <clipPath id="clip0_2552_5424">
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

function MedicalServicesIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <g clipPath="url(#clip0_2552_5505)">
        <path d="M20 6H16V4C16 2.9 15.1 2 14 2H10C8.9 2 8 2.9 8 4V6H4C2.9 6 2 6.9 2 8V20C2 21.1 2.9 22 4 22H20C21.1 22 22 21.1 22 20V8C22 6.9 21.1 6 20 6ZM10 4H14V6H10V4ZM16 15H13V18H11V15H8V13H11V10H13V13H16V15Z" fill="#DB3735" />
      </g>
      <defs>
        <clipPath id="clip0_2552_5505">
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

function StatusIconTooltip({ label, children }: { label: string; children: JSX.Element }) {
  return (
    <NJTooltip text={label} hasArrow placement="top">
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>{children}</span>
    </NJTooltip>
  );
}

function getStatusIcons(row: OptimizationRow): Array<JSX.Element> {
  if (row.id === 'opt-287156145-b') {
    return [
      <StatusIconTooltip key="warning" label="Slack">
        <WarningIcon />
      </StatusIconTooltip>,
      <StatusIconTooltip key="report" label="Infeasible">
        <ReportIcon />
      </StatusIconTooltip>,
    ];
  }

  if (row.id === 'opt-286716700') {
    return [
      <StatusIconTooltip key="warning" label="Slack">
        <WarningIcon />
      </StatusIconTooltip>,
    ];
  }

  if (row.id === 'opt-286716874-a') {
    return [
      <StatusIconTooltip key="medical-services" label="Erreur">
        <MedicalServicesIcon />
      </StatusIconTooltip>,
    ];
  }

  if (row.id === 'opt-286716874-b') {
    return [
      <StatusIconTooltip key="report" label="Infeasible">
        <ReportIcon />
      </StatusIconTooltip>,
    ];
  }

  return [];
}

function StatusCell({ value, row }: { value: OptimizationStatus; row: OptimizationRow }) {
  // En cours: information, Succès: success, En préparation: neutral, Échec: danger
  const badgeProps = {
    running: { variant: 'information' as const },
    success: { variant: 'success' as const },
    preparing: { variant: 'neutral' as const },
    failed: { variant: 'danger' as const },
  }[value];
  const statusIcons = getStatusIcons(row);
  return (
    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', height: '100%', gap: 8 }}>
      <NJBadge
        variant={badgeProps.variant}
        emphasis="subtle"
        scale="md"
        style={{
          borderRadius: '9999px',
          padding: '0 8px',
          height: 24,
          width: 'auto',
          minWidth: 'fit-content',
          whiteSpace: 'nowrap',
        }}
      >
        {STATUS_LABELS[value]}
      </NJBadge>
      {statusIcons.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {statusIcons}
        </div>
      )}
    </div>
  );
}

function TypeCell({ value }: { value: OptimizationType }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', height: '100%' }}>
      <NJTag
        label={TYPE_LABELS[value]}
        variant="ultramarine"
        size="xs"
        style={{
          height: 20,
          width: 'fit-content',
          maxWidth: '100%',
          whiteSpace: 'nowrap',
        }}
      />
    </div>
  );
}

const ATTRIBUTE_MAP: Record<string, { color: string; label: string; description: string }> = {
  P: {
    color: 'var(--nj-core-color-pink-300)',
    label: 'P',
    description: 'Réserve de puissance',
  },
  R: {
    color: 'var(--nj-core-color-teal-400)',
    label: 'R',
    description: 'Redimensionnement',
  },
  F: {
    color: 'var(--nj-core-color-purple-400)',
    label: 'F',
    description: 'Foisonnement par canalisation',
  },
  A: {
    color: '#0288d1',
    label: 'A',
    description: 'Foisonnement Automatique',
  },
  C: {
    color: '#d32f2f',
    label: 'C',
    description: 'Chemin Critique',
  },
};

const ATTRIBUTE_PRIORITY: Record<string, number> = {
  R: 0,
  P: 1,
  A: 2,
  C: 3,
  F: 4,
};

function AttributeIcon({ letter, showPlus }: { letter: string; showPlus?: boolean }) {
  const attr = ATTRIBUTE_MAP[letter];
  if (!attr) return null;

  const chip = (
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: attr.color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontWeight: 700,
        fontSize: 13,
        position: 'relative',
        cursor: 'default',
      }}
    >
      {letter}
      {showPlus && (
        <div
          style={{
            position: 'absolute',
            bottom: -2,
            right: -2,
            background: 'var(--nj-core-color-blue-400)',
            width: 14,
            height: 14,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 10,
            fontWeight: 700,
            border: '2px solid white',
          }}
        >
          +
        </div>
      )}
    </div>
  );

  return (
    <NJTooltip text={`${letter} - ${attr.description}`} hasArrow placement="top">
      {chip}
    </NJTooltip>
  );
}

function AttributesCell({ attributes }: { attributes: OptimizationAttribute[] }) {
  if (!attributes || attributes.length === 0) {
    return <span style={{ color: 'var(--nj-semantic-color-text-neutral-weak-default)' }}>—</span>;
  }

  const orderedAttributes = [...attributes].sort((left, right) => {
    const leftPriority = ATTRIBUTE_PRIORITY[left.label] ?? Number.MAX_SAFE_INTEGER;
    const rightPriority = ATTRIBUTE_PRIORITY[right.label] ?? Number.MAX_SAFE_INTEGER;
    return leftPriority - rightPriority;
  });

  const displayed = orderedAttributes.slice(0, 2);
  const remaining = orderedAttributes.slice(2);
  const hasMore = remaining.length > 0;
  const tooltipLines = remaining
    .map((attr) => {
      const details = ATTRIBUTE_MAP[attr.label];
      return details ? `${attr.label} - ${details.description}` : attr.label;
    })
    .join('\n');

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', height: '100%' }}>
      {displayed.map((attr) => (
        <AttributeIcon
          key={attr.id}
          letter={attr.label}
          showPlus={false}
        />
      ))}
      {hasMore && (
        <NJTooltip
          text={
            <span
              style={{
                display: 'inline-block',
                textAlign: 'left',
                whiteSpace: 'pre-line',
              }}
            >
              {tooltipLines}
            </span>
          }
          hasArrow
          placement="top"
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: 'var(--nj-core-color-blue-400)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: 700,
              fontSize: 13,
              cursor: 'default',
            }}
          >
            +
          </div>
        </NJTooltip>
      )}
    </div>
  );
}

function NameCell({
  value,
  description,
  onOpenDetails,
}: {
  value: string;
  description?: string;
  onOpenDetails: () => void;
}) {
  const nameContent = (
    <button
      type="button"
      onClick={onOpenDetails}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        border: 'none',
        background: 'transparent',
        padding: 0,
        cursor: 'pointer',
      }}
    >
      <NJText
        as="span"
        scale="sm"
        style={{
          color: 'var(--nj-semantic-color-text-brand-primary-default)',
          fontWeight: 600,
          textDecoration: 'underline',
          textDecorationColor: 'transparent',
        }}
      >
        {value}
      </NJText>
    </button>
  );

  if (!description) {
    return nameContent;
  }

  return (
    <NJTooltip text={description} hasArrow placement="top">
      {nameContent}
    </NJTooltip>
  );
}

interface ActionsCellProps {
  row: OptimizationRow;
  onClone: (row: OptimizationRow) => void;
  onPlay: (row: OptimizationRow) => void;
  onStop: (row: OptimizationRow) => void;
}

function ActionsCell({ row, onClone, onPlay, onStop }: ActionsCellProps) {
  const isRunning = row.status === 'running';
  const isPreparing = row.status === 'preparing';
  const isFinalStatus = row.status === 'failed' || row.status === 'success';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--nj-core-space-50)', height: '100%' }}>
      {/* Primary action: Clone (failed/success), Play (preparing), or Stop (running) */}
      {isPreparing && (
        <NJTooltip text="Lancer" hasArrow placement="top">
          <NJIconButton
            icon="play_circle_outline"
            label={`Lancer ${row.name}`}
            variant="tertiary"
            scale="xs"
            onClick={() => onPlay(row)}
          />
        </NJTooltip>
      )}
      {isRunning && (
        <NJTooltip text="Arrêter" hasArrow placement="top">
          <NJIconButton
            icon="stop_circle"
            label={`Arrêter ${row.name}`}
            variant="tertiary"
            scale="xs"
            onClick={() => onStop(row)}
          />
        </NJTooltip>
      )}
      {isFinalStatus && (
        <NJTooltip text="Cloner" hasArrow placement="top">
          <NJIconButton
            icon="content_copy"
            label={`Cloner ${row.name}`}
            variant="tertiary"
            scale="xs"
            onClick={() => onClone(row)}
          />
        </NJTooltip>
      )}

      <span
        aria-hidden="true"
        style={{
          width: '1px',
          height: '18px',
          background: 'var(--nj-semantic-color-border-neutral-minimal-default)',
        }}
      />

      <NJTooltip text="Supprimer" hasArrow placement="top">
        <NJIconButton
          icon="delete_outline"
          label={`Supprimer ${row.name}`}
          variant="tertiary"
          scale="xs"
        />
      </NJTooltip>
    </div>
  );
}

export default function CloneOptimisationPrototype() {
  const { data, isLoading, error } = useOptimizationCloneList();

  const [isPrimaryMenuFolded, setIsPrimaryMenuFolded] = useState(true);
  const [rows, setRows] = useState<OptimizationRow[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [selectedOptimization, setSelectedOptimization] = useState<OptimizationRow | null>(null);
  const [detailOptimization, setDetailOptimization] = useState<OptimizationRow | null>(null);
  const [activeDetailTab, setActiveDetailTab] = useState<DetailTab>('parameters');
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [formOpen, setFormOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState<string>('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [newFormType, setNewFormType] = useState<OptimizationType | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (data) {
      setRows(data.rows);
      setTotal(data.total);
    }
  }, [data]);

  const openCloneModal = (row: OptimizationRow) => {
    setFeedbackMessage('');
    setNewFormType(null);
    setSelectedOptimization(row);
    setFormOpen(true);
  };

  const openDetails = (row: OptimizationRow) => {
    setDetailOptimization(row);
    setActiveDetailTab('parameters');
    setOpenSections({});
  };

  const closeDetails = () => {
    setDetailOptimization(null);
    setActiveDetailTab('parameters');
    setOpenSections({});
  };

  const detailIndex = detailOptimization
    ? rows.findIndex((row) => row.id === detailOptimization.id)
    : -1;
  const previousDetail = detailIndex > 0 ? rows[detailIndex - 1] : null;
  const nextDetail =
    detailIndex >= 0 && detailIndex < rows.length - 1 ? rows[detailIndex + 1] : null;

  const expandAllSections = () => {
    const nextState: Record<string, boolean> = {};
    PARAMETERS_SECTIONS.forEach((section) => {
      nextState[section.id] = true;
    });
    setOpenSections(nextState);
  };

  const collapseAllSections = () => {
    setOpenSections({});
  };

  const openNewForm = (type: OptimizationType) => {
    setDropdownOpen(false);
    setFeedbackMessage('');
    setSelectedOptimization(null);
    setNewFormType(type);
    setFormOpen(true);
  };

  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  const handlePlay = (row: OptimizationRow) => {
    setFeedbackMessage(`Lancement de l'optimisation "${row.name}"...`);
    // TODO: API call to launch optimization
    setTimeout(() => {
      setRows((previous) =>
        previous.map((r) =>
          r.id === row.id ? { ...r, status: 'running' as OptimizationStatus } : r,
        ),
      );
      setFeedbackMessage(`Optimisation "${row.name}" lancée.`);
    }, 500);
  };

  const handleStop = (row: OptimizationRow) => {
    setFeedbackMessage(`Arrêt de l'optimisation "${row.name}"...`);
    // TODO: API call to stop optimization
    setTimeout(() => {
      setRows((previous) =>
        previous.map((r) =>
          r.id === row.id ? { ...r, status: 'failed' as OptimizationStatus } : r,
        ),
      );
      setFeedbackMessage(`Optimisation "${row.name}" arrêtée.`);
    }, 500);
  };

  const columns: ColDef<OptimizationRow>[] = useMemo(
    () => [
      {
        headerName: 'Status',
        field: 'status',
        minWidth: 160,
        maxWidth: 200,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: { filterOptions: ['contains'] },
        filterValueGetter: ({ data }) => (data ? STATUS_LABELS[data.status] : ''),
        cellRenderer: (params: ICellRendererParams<OptimizationRow, OptimizationStatus>) => {
          if (!params.data) {
            return null;
          }
          return <StatusCell value={params.value as OptimizationStatus} row={params.data} />;
        },
        valueFormatter: (params) => STATUS_LABELS[params.value as OptimizationStatus],
      },
      {
        headerName: 'Type',
        field: 'type',
        minWidth: 130,
        maxWidth: 160,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: { filterOptions: ['contains'] },
        cellRenderer: TypeCell,
        valueFormatter: (params) => TYPE_LABELS[params.value as OptimizationType],
      },
      {
        headerName: 'Name',
        field: 'name',
        minWidth: 280,
        flex: 2,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: { filterOptions: ['contains'] },
        cellRenderer: (params: ICellRendererParams<OptimizationRow, string>) => {
          if (!params.data) {
            return null;
          }

          return (
            <NameCell
              value={params.value as string}
              description={params.data.description}
              onOpenDetails={() => openDetails(params.data as OptimizationRow)}
            />
          );
        },
      },
      {
        headerName: 'Steps',
        field: 'steps',
        minWidth: 130,
        maxWidth: 160,
        filter: 'agNumberColumnFilter',
        floatingFilter: true,
      },
      {
        headerName: 'Attributes',
        field: 'attributes',
        minWidth: 150,
        maxWidth: 200,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterValueGetter: ({ data }) => data?.attributes.map((attribute) => attribute.label).join(' ') || '',
        cellRenderer: ({ value }: { value: OptimizationRow['attributes'] }) => (
          <AttributesCell attributes={value} />
        ),
      },
      {
        headerName: 'Created by',
        field: 'createdBy',
        minWidth: 160,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: { filterOptions: ['contains'] },
      },
      {
        headerName: 'Date',
        field: 'date',
        minWidth: 160,
        sort: 'desc',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: { filterOptions: ['contains'] },
      },
      {
        headerName: 'Actions',
        field: 'id',
        minWidth: 100,
        maxWidth: 130,
        sortable: false,
        filter: false,
        floatingFilter: false,
        cellRenderer: (params: ICellRendererParams<OptimizationRow>) => {
          if (!params.data) {
            return null;
          }
          return (
            <ActionsCell
              row={params.data}
              onClone={openCloneModal}
              onPlay={handlePlay}
              onStop={handleStop}
            />
          );
        },
      },
    ],
    [],
  );

  return (
    <PrototypeStudioLayout title={meta.title}>
      <style>
        {`
          .clone-opti-primary-sidebar {
            position: relative !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            height: 100% !important;
            min-width: 0 !important;
          }
          .hidden { display: none !important; }
          .clone-opti-primary-sidebar .nj-list-deprecated__item button {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            width: 100% !important;
            gap: 16px;
          }

          /* Secondary sidebar nav — minimal overrides, FDS native padding/gap/height preserved */
          .secondary-nav-item li {
            border: none !important;
            border-bottom: none !important;
            box-shadow: none !important;
          }
          .secondary-nav-item li:hover {
            background-color: var(--nj-core-color-blue-alpha-200) !important;
          }
          .secondary-nav-item.active {
            border-left: 3px solid var(--nj-semantic-color-border-brand-primary-default) !important;
          }
        `}
      </style>
      <div style={{ display: 'flex', flexDirection: 'row', height: '100vh', overflow: 'hidden' }}>
        {/* Primary sidebar — full viewport height */}
        <div
          style={{
            width: isPrimaryMenuFolded
              ? PRIMARY_SIDEBAR_WIDTH_FOLDED
              : PRIMARY_SIDEBAR_WIDTH_OPEN,
            flexShrink: 0,
            height: '100%',
            overflow: 'hidden',
            transition: 'width 180ms ease',
          }}
        >
          <NJSidebarDeprecated
            isFolded={isPrimaryMenuFolded}
            className="min-h-screen clone-opti-primary-sidebar"
          >
            <NJSidebarBrandDeprecated
              imageSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='24' viewBox='0 0 80 24'%3E%3Ctext y='18' font-size='18' font-family='Lato,sans-serif' font-weight='700' fill='%230052CC'%3ENeMo%3C/text%3E%3C/svg%3E"
              imageFoldedSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='30' height='30' viewBox='0 0 30 30'%3E%3Crect x='.25' y='.25' width='29.5' height='29.5' rx='5.75' fill='white' stroke='%237FADEF' stroke-width='.5'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M21.472 16.7C21.472 18.07 21.045 19.34 20.318 20.38H9.747C9.02 19.34 8.593 18.07 8.593 16.7c0-3.558 2.883-6.44 6.44-6.44 1.343 0 2.591.412 3.623 1.116.87-.302 1.882-.636 3.053-1.004C19.033 8.603 16.662 7.5 15.033 7.5 9.952 7.5 5.833 11.62 5.833 16.7c0 2.508 1.003 4.782 2.63 6.442h13.14C23.229 21.482 24.232 19.21 24.232 16.7c0-1.136-.206-2.224-.582-3.228-.855.786-1.614 1.466-2.285 2.051.07.382.107.775.107 1.177zm-8.08.546c.508.88 1.623 1.187 2.49.686.868-.5 2.912-2.212 6.934-6.128-5.402 1.525-7.906 2.44-8.773 2.941-.868.5-1.159 1.62-.651 2.5z' fill='url(%23g)'/%3E%3Cdefs%3E%3ClinearGradient id='g' x1='-2.85' y1='15.16' x2='12' y2='32.62' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%2300AAFF'/%3E%3Cstop offset='.97' stop-color='%2323D2B5'/%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E"
              className="cursor-pointer"
            />

            <NJSidebarMenuDeprecated isFolded>
              <>
                {PRIMARY_NAV.map((item) => (
                  <li
                    key={item.id}
                    className={`nj-list-deprecated__item nj-list-deprecated__item--right-border nj-list-deprecated__item--clickable${item.id === 'networks' ? ' nj-list-deprecated__item--active' : ''}`}
                    style={{ overflow: 'hidden' }}
                  >
                    <button type="button" aria-label={item.label} style={{ overflow: 'hidden' }}>
                      {item.id === 'coreai' ? (
                        <span className="nj-list-deprecated__item-icon">
                          <NJIcon name="engie_ai" size="md" variant="ai" />
                        </span>
                      ) : (
                        <span
                          className="material-icons nj-icon-material nj-icon-material--md nj-icon-material--grey nj-list-deprecated__item-icon"
                          aria-hidden="true"
                        >
                          {item.icon}
                        </span>
                      )}
                      <div className={`nj-list-deprecated__item-content${isPrimaryMenuFolded ? ' hidden' : ''}`} style={{ color: 'var(--nj-core-color-grey-600)' }}>{item.label}</div>
                    </button>
                  </li>
                ))}
              </>
            </NJSidebarMenuDeprecated>

            <NJSidebarMenuDeprecated isFolded isFooterNav>
              <>
                <li
                  className="nj-list-deprecated__item nj-list-deprecated__item--right-border nj-list-deprecated__item--clickable"
                  style={{ overflow: 'hidden' }}
                >
                  <button type="button" aria-label="Old version" style={{ overflow: 'hidden' }}>
                    <span
                      className="material-icons nj-icon-material nj-icon-material--md nj-icon-material--grey nj-list-deprecated__item-icon"
                      aria-hidden="true"
                    >
                      history
                    </span>
                    <div className={`nj-list-deprecated__item-content${isPrimaryMenuFolded ? ' hidden' : ''}`} style={{ color: 'var(--nj-core-color-grey-600)' }}>Old version</div>
                  </button>
                </li>
                <NJSidebarDividerDeprecated />
                {PRIMARY_FOOTER.map((item) => (
                  <li
                    key={item.id}
                    className="nj-list-deprecated__item nj-list-deprecated__item--right-border nj-list-deprecated__item--clickable"
                    style={{ overflow: 'hidden' }}
                  >
                    <button type="button" aria-label={item.label} style={{ overflow: 'hidden' }}>
                      <span
                        className="material-icons nj-icon-material nj-icon-material--md nj-icon-material--grey nj-list-deprecated__item-icon"
                        aria-hidden="true"
                      >
                        {item.icon}
                      </span>
                      <div className={`nj-list-deprecated__item-content${isPrimaryMenuFolded ? ' hidden' : ''}`} style={{ color: 'var(--nj-core-color-grey-600)' }}>{item.label}</div>
                    </button>
                  </li>
                ))}
              </>
            </NJSidebarMenuDeprecated>

            <NJSidebarCloseDeprecated
              data-testid="toggle_main_navbar"
              onClick={() => setIsPrimaryMenuFolded((previous) => !previous)}
            >
              <span className="text-[var(--nj-core-color-grey-600)]">
                {isPrimaryMenuFolded ? 'Expand' : 'Reduce'}
              </span>
            </NJSidebarCloseDeprecated>
          </NJSidebarDeprecated>
        </div>

        {/* Right column: header + content + footer */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, background: 'var(--nj-semantic-color-background-neutral-secondary-default)' }}>
        {/* App header bar */}
        <header
          style={{
            height: 48,
            borderBottom: '1px solid var(--nj-core-color-grey-300)',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            background: 'var(--nj-semantic-color-background-neutral-primary-default)',
            flexShrink: 0,
            zIndex: 100,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center' }}>
            {/* Study badge */}
            <div
              style={{
                fontSize: 12,
                padding: '8px 12px 0',
                background: 'var(--nj-semantic-color-background-neutral-tertiary-translucent-default)',
                alignSelf: 'stretch',
                display: 'flex',
                alignItems: 'flex-start',
              }}
            >
              <span>Study</span>
            </div>
            {/* Vertical dashed separator */}
            <div
              style={{
                margin: '0 16px',
                borderLeft: '1px dashed var(--nj-core-color-grey-300)',
                alignSelf: 'stretch',
              }}
            />
            {/* Actions */}
            <div style={{ display: 'flex', flexDirection: 'row', marginRight: 16, alignItems: 'center', gap: 4 }}>
              <NJIconButton icon="search" label="search action" variant="tertiary" />
              <NJIconButton icon="more_time" label="more time action" variant="tertiary" />
              {/* Language selector */}
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <button
                  type="button"
                  style={{
                    fontSize: 16,
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--nj-semantic-color-text-neutral-tertiary-default)',
                    background: 'var(--nj-semantic-color-background-neutral-primary-default)',
                  }}
                >
                  FR
                </button>
                <NJIconButton icon="keyboard_arrow_down" label="IconButton" variant="tertiary" scale="sm" />
              </div>
            </div>
          </div>
        </header>

        {/* Content row: secondary sidebar + main */}
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Secondary sidebar */}
        <nav style={{ width: 230, background: 'var(--nj-semantic-color-background-neutral-primary-default)', borderRight: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '24px 0 12px 24px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ flexShrink: 0 }}>
              <g clipPath="url(#clip0_2552_5329)">
                <path d="M17.7143 2H6.28571C5.02857 2 4.01143 3 4.01143 4.22222L4 22L12 18.6667L20 22V4.22222C20 3 18.9714 2 17.7143 2Z" fill="#007ACD" />
                <path d="M14.9844 11.6357C14.9843 11.6337 14.9841 11.6318 14.9839 11.6299C14.9083 10.8171 14.4723 8.52986 11.9999 6C9.52643 8.53123 9.09116 10.8194 9.01576 11.6311L9.01551 11.6341C8.99751 11.8292 9.00007 11.9385 9.00019 11.9436V11.9448C9.00019 13.6321 10.3432 15 12 15C13.6568 15 15 13.6321 15 11.9448L14.9998 11.9424C15 11.9323 15.0019 11.8249 14.9844 11.6357Z" fill="white" />
              </g>
              <defs>
                <clipPath id="clip0_2552_5329">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
            <NJHeading
              scale="sm"
              as="h2"
              style={{
                margin: 0,
                fontSize: 14,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              test PUC
            </NJHeading>
          </div>
          <NJListDeprecated style={{ paddingTop: 12, paddingBottom: 16 }}>
            <>
              {SECONDARY_NAV.filter((item) => item.id !== 'optimizations').map((item) => (
                <div key={item.id} className="secondary-nav-item">
                  <NJListItemDeprecated
                    icon={{ name: item.icon, size: 'md' }}
                    type="link"
                    className="border-none"
                  >
                    {item.label}
                  </NJListItemDeprecated>
                </div>
              ))}
              <div className="secondary-nav-item active" style={{ position: 'relative' }}>
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 3,
                    backgroundColor: 'var(--nj-semantic-color-border-brand-primary-default)',
                    zIndex: 1,
                  }}
                />
                <NJListItemDeprecated
                  icon={{ name: 'stacked_bar_chart', size: 'md' }}
                  type="link"
                  isActive
                  className="border-none"
                >
                  Optimizations
                </NJListItemDeprecated>
              </div>
            </>
          </NJListDeprecated>
        </nav>

        {/* Main content area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* Main content */}
          <main
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '24px',
              background: 'var(--nj-semantic-color-background-neutral-secondary-default)',
            }}
          >
            {detailOptimization ? (
              <div style={{ display: 'grid', gap: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <NJButton label="Retour à la liste" emphasis="minimal" icon="arrow_back" onClick={closeDetails} />
                    <NJIconButton
                      icon="navigate_before"
                      label="Optimisation précédente"
                      variant="tertiary"
                      isDisabled={!previousDetail}
                      onClick={() => previousDetail && openDetails(previousDetail)}
                    />
                    <NJIconButton
                      icon="navigate_next"
                      label="Optimisation suivante"
                      variant="tertiary"
                      isDisabled={!nextDetail}
                      onClick={() => nextDetail && openDetails(nextDetail)}
                    />
                    <NJIcon name="stacked_bar_chart" />
                    <NJHeading scale="sm" as="h3" style={{ margin: 0, fontSize: 22, whiteSpace: 'nowrap' }}>
                      {detailOptimization.name}
                    </NJHeading>
                    <NJBadge
                      variant={detailOptimization.status === 'success' ? 'success' : detailOptimization.status === 'running' ? 'information' : detailOptimization.status === 'preparing' ? 'neutral' : 'danger'}
                      emphasis="subtle"
                      scale="md"
                      style={{ borderRadius: '9999px', padding: '0 8px', height: 24 }}
                    >
                      {STATUS_LABELS[detailOptimization.status]}
                    </NJBadge>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <NJIconButton icon="calculate" label="Calculer" variant="tertiary" scale="sm" />
                    <NJIconButton icon="delete" label="Supprimer" variant="tertiary" scale="sm" />
                  </div>
                </div>

                <div style={{ borderBottom: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)' }}>
                  <NJTabs
                    label="Navigation optimisation"
                    activeTab={activeDetailTab}
                    onClickTabItem={(id: string) => setActiveDetailTab(id as DetailTab)}
                  >
                    {DETAIL_TABS.map((tab) => (
                      <NJTab
                        key={tab.id}
                        id={tab.id}
                        label={tab.label}
                        ariaControls={`panel-${tab.id}`}
                        isActive={activeDetailTab === tab.id}
                      />
                    ))}
                  </NJTabs>
                </div>

                {activeDetailTab === 'parameters' ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                      <NJButton label="Tout Développer" variant="secondary" onClick={expandAllSections} />
                      <NJButton label="Tout Réduire" variant="secondary" onClick={collapseAllSections} />
                    </div>

                    <div
                      style={{
                        background: 'var(--nj-semantic-color-background-neutral-primary-default)',
                        border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                        borderRadius: 'var(--nj-core-border-radius-md)',
                        padding: 16,
                      }}
                    >
                      {PARAMETERS_SECTIONS.map((section) => (
                        <NJAccordionItem
                          key={section.id}
                          className="mt-3"
                          hasLeadingToggleIcon
                          open={Boolean(openSections[section.id])}
                          onToggle={(event) => {
                            const isOpen = event?.currentTarget?.open;
                            setOpenSections((previous) => ({
                              ...previous,
                              [section.id]: Boolean(isOpen),
                            }));
                          }}
                        >
                          <NJAccordionItemHeader>{section.title}</NJAccordionItemHeader>
                          <NJAccordionItemContent>
                            <div style={{ display: 'grid', gap: 10 }}>
                              {section.rows.map((row) => (
                                <div
                                  key={`${section.id}-${row.label}`}
                                  style={{
                                    display: 'grid',
                                    gridTemplateColumns: '240px 1fr',
                                    alignItems: 'center',
                                    gap: 12,
                                  }}
                                >
                                  <NJText scale="sm" style={{ color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}>
                                    {row.label}
                                  </NJText>
                                  <NJText scale="sm">{row.value}</NJText>
                                </div>
                              ))}
                            </div>
                          </NJAccordionItemContent>
                        </NJAccordionItem>
                      ))}
                    </div>
                  </>
                ) : (
                  <NJInlineMessage variant="information">
                    Cet onglet est simplifié dans ce prototype. Le parcours demandé est centré sur Paramètres.
                  </NJInlineMessage>
                )}
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <NJHeading scale="md" style={{ fontSize: 16, lineHeight: '24px', margin: 0 }}>
                Optimisations list ({total})
              </NJHeading>
              {/* Create new optimisation dropdown */}
              <div ref={dropdownRef} style={{ position: 'relative' }}>
                <div
                  style={{
                    display: 'flex',
                    gap: 0,
                    borderRadius: 'var(--nj-core-border-radius-sm)',
                    overflow: 'hidden',
                    background: 'var(--nj-semantic-color-background-brand-primary-default)',
                  }}
                >
                  <NJButton
                    label="Créer une nouvelle optimisation"
                    variant="primary"
                    scale="sm"
                    style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                    onClick={() => setDropdownOpen((o) => !o)}
                  />
                  <div style={{ width: '1px', background: 'rgba(255,255,255,0.4)', alignSelf: 'stretch' }} />
                  <NJIconButton
                    icon="keyboard_arrow_down"
                    label="Options"
                    variant="primary"
                    scale="sm"
                    onClick={() => setDropdownOpen((o) => !o)}
                  />
                </div>
                {dropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: 4,
                      background: 'var(--nj-semantic-color-background-neutral-primary-default)',
                      border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                      borderRadius: 'var(--nj-core-border-radius-md)',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                      minWidth: 240,
                      zIndex: 100,
                      overflow: 'hidden',
                    }}
                  >
                    {[
                      { type: 'timeless' as OptimizationType, label: 'Paramétrique Atemporelle', icon: 'access_time' },
                      { type: 'short-term' as OptimizationType, label: 'Court terme', icon: 'timelapse' },
                      { type: 'trajectory' as OptimizationType, label: 'Trajectoire', icon: 'show_chart' },
                    ].map((option) => (
                      <button
                        key={option.type}
                        onClick={() => openNewForm(option.type)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          width: '100%',
                          padding: '12px 16px',
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          textAlign: 'left',
                          fontSize: 14,
                          color: 'var(--nj-semantic-color-text-neutral-primary-default)',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--nj-semantic-color-background-neutral-secondary-default)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <NJIcon name={option.icon} style={{ fontSize: 20, color: 'var(--nj-semantic-color-text-brand-primary-default)', flexShrink: 0 }} />
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {feedbackMessage && (
              <div
                style={{
                  marginBottom: 16,
                  padding: '12px 16px',
                  background: '#e8f5e9',
                  borderRadius: 'var(--nj-core-border-radius-sm)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  border: '1px solid #a5d6a7',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flex: 1,
                  }}
                >
                  <NJIcon name="check_circle" style={{ color: '#2e7d32', flexShrink: 0 }} />
                  <span
                    style={{
                      color: '#2e7d32',
                      fontSize: '13px',
                      fontWeight: 500,
                    }}
                  >
                    {feedbackMessage}
                  </span>
                </div>
                <NJIconButton
                  icon="close"
                  label="Close message"
                  variant="tertiary"
                  scale="xs"
                  onClick={() => setFeedbackMessage('')}
                />
              </div>
            )}

            <NJCard
              style={{
                background: 'var(--nj-semantic-color-background-neutral-primary-default)',
                border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                borderRadius: 'var(--nj-core-border-radius-md)',
                padding: 0,
              }}
            >
              {isLoading && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
                  <NJSpinner />
                </div>
              )}
              {error && (
                <NJInlineMessage variant="error">Unable to load the optimisations list.</NJInlineMessage>
              )}
              {!isLoading && !error && (
                <DataTable<OptimizationRow> testId="clone-optimisation-table" header={columns} data={rows} />
              )}
            </NJCard>
              </>
            )}
          </main>
        </div>{/* end main content area */}
        </div>{/* end content row */}

      {/* App footer — exact match of the real Nemo footer */}
      <footer
        style={{
          minHeight: 48,
          display: 'flex',
          alignItems: 'center',
          background: 'var(--nj-semantic-color-background-neutral-secondary-default)',
          borderTop: '1px solid var(--nj-core-color-grey-300)',
          flexShrink: 0,
        }}
        role="contentinfo"
      >
        {/* ENGIE logo */}
        <div style={{ flex: '0 0 25%', paddingLeft: 24 }}>
          <svg width="51" height="18" viewBox="0 0 51 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="ENGIE-LOGO">
            <path fillRule="evenodd" clipRule="evenodd" d="M50.1383 7.19874C43.4203 2.71327 34.6567 0 25.0699 0C15.4831 0 6.71799 2.71327 0 7.19874C6.68185 4.97722 14.4933 3.59937 25.0699 3.59937C35.6465 3.59937 43.458 4.97722 50.1383 7.19874ZM21.2255 9.53023C20.7321 9.53023 20.4792 9.92929 20.4148 10.4179C20.3783 10.6908 20.2877 11.3029 20.1989 11.9032L20.1987 11.9048L20.1986 11.9055C20.1116 12.4934 20.0264 13.0694 19.9953 13.304L19.9883 13.358C19.954 13.6266 19.9198 13.8946 19.6606 14.0094C19.3841 14.132 19.0479 13.9434 18.762 13.6952L14.2309 9.84288C13.9976 9.63958 13.6983 9.52788 13.3888 9.52866C12.7478 9.52866 12.132 9.92615 12.132 10.8987V16.9238C12.1076 17.3239 12.4124 17.668 12.8124 17.6924C12.8281 17.6933 12.8437 17.6936 12.8594 17.6936C13.2705 17.7046 13.6173 17.3891 13.6449 16.9788C13.6449 16.9788 14.1163 14.1084 14.1681 13.7879C14.2341 13.4155 14.2922 13.1233 14.652 13.0118C14.9772 12.9112 15.2804 13.0998 15.7423 13.5004C16.2042 13.901 20.0157 17.3464 20.0157 17.3464C20.261 17.5852 20.5945 17.7111 20.9364 17.6936C21.3119 17.6936 21.9937 17.5365 21.9937 16.3928V10.3912C21.9953 9.83502 21.6449 9.53023 21.2255 9.53023ZM41.4234 13.5459C41.4234 15.9026 45.2349 16.6677 48.564 15.9513C49.0369 15.8492 49.3527 15.9953 49.4533 16.3865C49.568 16.8217 49.2145 17.1029 48.8248 17.2364C44.7117 18.6536 39.0982 17.4894 39.0982 13.6748C39.0982 10.9851 41.4925 9.33856 44.7918 9.33856C48.4383 9.33856 49.6842 11.4203 49.6842 12.795C49.6842 13.6638 49.3134 14.0848 48.5577 14.0848C48.0379 14.0834 47.278 14.0787 46.5187 14.074C45.6095 14.0684 44.7013 14.0628 44.2074 14.0628C43.5742 14.0597 43.3386 13.7015 43.3386 13.3873C43.3386 12.9898 43.6324 12.696 44.1822 12.6756C44.4241 12.6665 44.6783 12.6575 44.9331 12.6484L44.9332 12.6484C45.698 12.6213 46.469 12.5939 46.9332 12.5656C47.6072 12.5263 47.7031 12.0408 47.3606 11.6842C46.8075 11.1139 45.7408 10.828 44.3755 10.9364C43.0746 11.0416 41.4234 11.7659 41.4234 13.5459ZM9.9293 15.9513C6.59859 16.6677 2.78869 15.9026 2.78869 13.5459C2.78869 11.7659 4.43833 11.0416 5.74077 10.9364C7.10448 10.828 8.17125 11.1139 8.72584 11.6842C9.06834 12.0408 8.97251 12.5263 8.30008 12.5656C7.83494 12.5938 7.06441 12.6212 6.30009 12.6484L6.29993 12.6484C6.04456 12.6575 5.78988 12.6665 5.54753 12.6756C4.99764 12.696 4.70385 12.9898 4.70385 13.3873C4.70385 13.7015 4.93951 14.0597 5.57266 14.0628C6.06659 14.0628 6.97514 14.0684 7.88462 14.074L7.88507 14.074H7.88525L7.88558 14.074C8.64499 14.0787 9.40495 14.0834 9.92459 14.0848C10.6787 14.0848 11.0511 13.6638 11.0511 12.795C11.0511 11.4203 9.80361 9.33856 6.15711 9.33856C2.85782 9.33856 0.463472 10.9851 0.463472 13.6748C0.463472 17.4894 6.07698 18.6536 10.1917 17.2364C10.5813 17.1029 10.9348 16.8217 10.8201 16.3865C10.718 15.9953 10.4022 15.8492 9.9293 15.9513ZM36.0105 16.3146C35.9318 15.5205 35.7838 14.0264 35.7219 12.2828C35.7213 12.2206 35.7648 12.1667 35.8256 12.154L37.9937 11.8036C38.013 11.7972 38.0336 11.7957 38.0537 11.7994C38.1194 11.8115 38.1629 11.8746 38.1508 11.9403C38.055 13.9827 37.8633 16.033 37.7973 16.6991C37.7266 17.4925 37.3323 17.6952 36.9254 17.6952C36.5185 17.6952 36.1178 17.4784 36.0471 16.6928C36.0382 16.595 36.0256 16.4679 36.0105 16.3147L36.0105 16.3146ZM36.9317 9.33385C36.2709 9.29221 35.7015 9.79418 35.6599 10.4548C35.657 10.5001 35.6567 10.5455 35.6591 10.5907C35.6591 10.6803 35.6701 10.9254 35.6842 11.2317C35.6883 11.3054 35.7511 11.3618 35.8248 11.3577C35.8284 11.3575 35.8319 11.3572 35.8354 11.3567C35.8374 11.3565 35.8393 11.3562 35.8413 11.3558L38.1037 10.9882C38.1653 10.9785 38.2115 10.9265 38.2137 10.8641C38.2137 10.7573 38.2137 10.6488 38.2231 10.597C38.2504 9.92599 37.7287 9.35977 37.0577 9.33243C37.0157 9.3307 36.9736 9.33117 36.9317 9.33385ZM31.7516 12.6593C32.5508 12.6546 33.2528 12.6504 33.6072 12.6504C34.3975 12.6504 34.8672 13.0039 34.8641 13.9261C34.8641 15.7392 33.0086 17.813 29.1987 17.813C25.7863 17.813 23.0495 16.4399 23.0495 13.6119C23.0495 10.9694 25.7958 9.33385 29.2223 9.33385C30.3049 9.31955 31.3848 9.44838 32.4336 9.71719C33.0118 9.86959 33.2993 10.1524 33.1815 10.5923C33.0715 11.0071 32.6316 11.0872 32.1838 11.0243C27.0951 10.3032 25.381 11.9984 25.381 13.5381C25.381 15.1752 27.3826 16.2089 29.2019 16.2089C30.5923 16.2089 31.934 15.7392 32.4855 15.0228C32.7604 14.6646 32.7494 14.2466 31.9372 14.2058C31.675 14.1921 31.2798 14.1728 30.8326 14.151C30.2508 14.1226 29.5808 14.0899 29.0008 14.0597C28.3268 14.0251 28.132 13.6638 28.132 13.3841C28.132 13.062 28.3126 12.6724 29.0353 12.6724C29.8248 12.6707 30.8501 12.6647 31.7516 12.6593Z" fill="#00AAFF" />
          </svg>
        </div>
        {/* App version (empty in prod too) */}
        <div style={{ flex: '0 0 25%' }} />
        {/* Links */}
        <div style={{ marginRight: 80, flex: '1 1 auto', display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', gap: 24, fontSize: 11 }}>
          <div>
            <a href="https://jira.tools.digital.engie.com/servicedesk/customer/portal/28" target="_blank" rel="noreferrer" className="nj-link nj-link--contextual" data-testid="help_desk" style={{ fontSize: 11 }}>
              Contacter le support
            </a>
          </div>
          <div>
            <a className="nj-link nj-link--contextual" href="#" data-testid="confidentiality_policy" style={{ fontSize: 11 }}>
              Politique de confidentialité
            </a>
          </div>
          <div>
            <a className="nj-link nj-link--contextual" href="#" data-testid="cookie_policy" style={{ fontSize: 11 }}>
              Politique de cookies
            </a>
          </div>
        </div>
      </footer>
        </div>{/* end right column */}
    </div>{/* end root flex-row */}

      {formOpen && (
        <>
          {/* Full-screen backdrop — dims the whole app, click to dismiss */}
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9998,
              background: 'rgba(0, 0, 0, 0.45)',
            }}
            onClick={() => { setFormOpen(false); setSelectedOptimization(null); setNewFormType(null); }}
          />
          {/* Right-side panel */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: `min(1300px, calc(100vw - ${isPrimaryMenuFolded ? PRIMARY_SIDEBAR_WIDTH_FOLDED : PRIMARY_SIDEBAR_WIDTH_OPEN}px))`,
              zIndex: 9999,
              background: 'var(--nj-semantic-color-background-neutral-secondary-default)',
              overflow: 'auto',
              boxShadow: '-4px 0 32px rgba(0, 0, 0, 0.18)',
            }}
          >
            {(selectedOptimization?.type ?? newFormType) === 'timeless' ? (
              <NewOptiFormTimelessEauFroide
                embedded
                initialValues={selectedOptimization ? {
                  name: buildCloneName(selectedOptimization.name),
                  description: selectedOptimization.description ?? '',
                  steps: selectedOptimization.seedForm.steps,
                  regulationMode: selectedOptimization.seedForm.regulationMode,
                } : undefined}
                onClose={() => { setFormOpen(false); setSelectedOptimization(null); setNewFormType(null); }}
              />
            ) : (selectedOptimization?.type ?? newFormType) === 'trajectory' ? (
              <TrajectoryOptimizationCreateForm
                embedded
                initialValues={selectedOptimization ? {
                  name: buildCloneName(selectedOptimization.name),
                  description: selectedOptimization.description ?? '',
                  startDate: toDateTimeLocalValue(selectedOptimization.seedForm.startDate),
                  steps: selectedOptimization.seedForm.steps,
                  regulationMode: selectedOptimization.seedForm.regulationMode,
                } : undefined}
                onClose={() => { setFormOpen(false); setSelectedOptimization(null); setNewFormType(null); }}
              />
            ) : (selectedOptimization?.type ?? newFormType) === 'short-term' ? (
              <NewOptiFormShortTerm
                embedded
                initialValues={selectedOptimization ? {
                  name: buildCloneName(selectedOptimization.name),
                  description: selectedOptimization.description ?? '',
                  startDate: toDateTimeLocalValue(selectedOptimization.seedForm.startDate),
                  steps: selectedOptimization.seedForm.steps,
                  regulationMode: selectedOptimization.seedForm.regulationMode,
                } : undefined}
                onClose={() => { setFormOpen(false); setSelectedOptimization(null); setNewFormType(null); }}
              />
            ) : null}
          </div>
        </>
      )}
    </PrototypeStudioLayout>
  );
}
